import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { HashLoader } from 'react-spinners';
import { fmtDate } from '../utils/format';
import { useTheme } from '../context/ThemeContext';

// ── Body scroll lock ─────────────────────────────────────────────────────────
// Every full-screen overlay (Modal, DetailPanel, ConfirmDialog) is a fixed
// backdrop that doesn't itself cover the page's scroll container, so without
// this the page behind it stays scrollable via the mouse wheel, keyboard, or
// dragging the native scrollbar while the overlay is open. Locking/restoring
// document.body's overflow keeps focus on the dialog like a real modal.
function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

// ── Toast ────────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="sms-toast-wrap">
        {toasts.map(t => (
          <div key={t.id} className={`sms-toast ${t.type}`}>
            <i className={`bi ${t.type === 'success' ? 'bi-check-circle-fill' : t.type === 'error' ? 'bi-x-circle-fill' : 'bi-info-circle-fill'}`}
               style={{ color: t.type === 'success' ? '#22c55e' : t.type === 'error' ? '#ef4444' : 'var(--sms-accent)' }} />
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() { return useContext(ToastContext); }

// ── Searchable Select ─────────────────────────────────────────────────────────
export function SearchableSelect({ value, onChange, options = [], placeholder = '-- Select --', className = '', id, style, disabled }) {
  const [open,  setOpen]  = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [dropUp, setDropUp] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const containerRef      = React.useRef(null);
  const menuRef            = React.useRef(null);
  const inputRef          = React.useRef(null);
  const optionRefs        = React.useRef([]);
  const menuMaxHeight     = 220;

  // Menu is portaled to document.body with position:fixed (see below) so a
  // scrollable ancestor (e.g. CapacityResolveModal's row list) can't clip
  // it — ancestor overflow:hidden/auto clips absolutely-positioned
  // descendants regardless of z-index, only escaping the ancestor's own DOM
  // subtree (via a portal) avoids that. containerRef no longer contains the
  // menu once portaled, so outside-click detection needs menuRef too.
  const updateMenuPos = React.useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const goUp = spaceBelow < menuMaxHeight + 8 && spaceAbove > spaceBelow;
    setDropUp(goUp);
    setMenuPos({
      left: rect.left, width: rect.width,
      top: goUp ? undefined : rect.bottom + 4,
      bottom: goUp ? (window.innerHeight - rect.top + 4) : undefined,
    });
  }, []);

  React.useEffect(() => {
    function onDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)
          && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false); setQuery('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, updateMenuPos]);

  const allOptions = [{ value: '', label: placeholder }, ...options];
  const q = query.toLowerCase();
  const filtered = allOptions.filter(o =>
    o.label.toLowerCase().includes(q) || (o.sublabel || '').toLowerCase().includes(q) || (o.right || '').toLowerCase().includes(q)
  );
  const filteredSignature = filtered.map(o => `${o.value}|${o.label}|${o.sublabel || ''}|${o.right || ''}|${o.disabled ? 1 : 0}`).join('\u0001');
  const filteredRef = React.useRef(filtered);
  filteredRef.current = filtered;
  const selected   = options.find(o => String(o.value) === String(value));
  const displayLabel = selected ? selected.label : '';
  const displayRight = selected ? selected.right : '';

  // Keep the keyboard highlight useful after typing filters the list. Prefer
  // the current value when it remains visible; otherwise start at the first
  // enabled option rather than leaving the highlight on a hidden row.
  React.useEffect(() => {
    if (!open) return;
    const currentFiltered = filteredRef.current;
    const selectedIndex = currentFiltered.findIndex(o => String(o.value) === String(value) && !o.disabled);
    const firstEnabledIndex = currentFiltered.findIndex(o => !o.disabled);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : Math.max(firstEnabledIndex, 0));
  }, [open, query, value, filteredSignature]);

  React.useEffect(() => {
    if (!open || !optionRefs.current[highlightedIndex]) return;
    optionRefs.current[highlightedIndex].scrollIntoView({ block: 'nearest' });
  }, [open, highlightedIndex]);

  const moveHighlight = direction => {
    if (!filtered.length) return;
    setHighlightedIndex(current => {
      let next = Number.isInteger(current) ? current : 0;
      for (let i = 0; i < filtered.length; i += 1) {
        next = (next + direction + filtered.length) % filtered.length;
        if (!filtered[next].disabled) return next;
      }
      return current;
    });
  };

  const selectHighlighted = () => {
    const option = filtered[highlightedIndex] || filtered.find(o => !o.disabled);
    if (option && !option.disabled) pick(option.value);
  };

  const pick = (val) => { onChange({ target: { value: val } }); setOpen(false); setQuery(''); };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={containerRef} id={id} style={{ position: 'relative' }}>
      <div
        className={`sms-form-control ${className}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          // Disabled state used to be a flat opacity fade, which blends text/
          // border down to a borderline (and on some screens sub-4.5:1) ratio
          // against the surface behind it. Swapping to --sms-sur2/--sms-txt2 —
          // both already audited ≥10:1 on both themes (see index.css header) —
          // keeps it readable and WCAG AA-compliant while still reading as
          // visually "disabled" via the surface/border shift.
          ...(disabled ? { background: 'var(--sms-sur2)', borderColor: 'var(--sms-bdr)' } : {}),
          ...style,
        }}
        onClick={openDropdown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        aria-haspopup="listbox"
        onKeyDown={e => {
          if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openDropdown(); }
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--sms-txt)', flex: 1, minWidth: 0, padding: 0 }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
              if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
              if (e.key === 'Home') { e.preventDefault(); setHighlightedIndex(filtered.findIndex(o => !o.disabled)); }
              if (e.key === 'End') { e.preventDefault(); setHighlightedIndex([...filtered].map(o => o.disabled).lastIndexOf(false)); }
              if (e.key === 'Enter') { e.preventDefault(); selectHighlighted(); }
            }}
          />
        ) : (
          <span style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
            <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
              {selected?.icon}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: displayLabel ? (disabled ? 'var(--sms-txt2)' : 'var(--sms-txt)') : 'var(--sms-txt3)' }}>
                {displayLabel || placeholder}
              </span>
            </span>
            {displayRight && <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--sms-txt3)' }}>{displayRight}</span>}
          </span>
        )}
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 12, color: 'var(--sms-txt3)', flexShrink: 0 }} aria-hidden="true" />
      </div>

      {open && menuPos && createPortal(
        <div ref={menuRef} role="listbox" style={{
          position: 'fixed', left: menuPos.left, width: menuPos.width, zIndex: 2000,
          ...(dropUp ? { bottom: menuPos.bottom } : { top: menuPos.top }),
          background: 'var(--sms-sur)', border: '1px solid var(--sms-bdr)',
          borderRadius: 8, boxShadow: 'var(--sms-shadow-dd)',
          maxHeight: menuMaxHeight, overflowY: 'auto',
        }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--sms-txt3)' }}>No options found</div>
            : filtered.map((o, index) => {
                const isSel = String(o.value) === String(value);
                const isHighlighted = index === highlightedIndex;
                return (
                  <div key={String(o.value)} ref={el => { optionRefs.current[index] = el; }} role="option" aria-selected={isSel} aria-disabled={o.disabled || undefined}
                    onClick={() => { if (!o.disabled) pick(o.value); }}
                    style={{ padding: '8px 12px', fontSize: 13, cursor: o.disabled ? 'not-allowed' : 'pointer', fontStyle: o.value === '' ? 'italic' : 'normal',
                      color: o.disabled ? 'var(--sms-txt3)' : isSel ? 'var(--sms-acc)' : o.value === '' ? 'var(--sms-txt3)' : 'var(--sms-txt)',
                      opacity: o.disabled ? 0.55 : 1,
                      background: isHighlighted || isSel ? 'var(--sms-acc-bg)' : 'transparent',
                      borderLeft: isHighlighted || isSel ? '3px solid var(--sms-acc)' : '3px solid transparent',
                    }}
                    onMouseEnter={e => { if (!o.disabled) e.currentTarget.style.background = 'var(--sms-sur2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isHighlighted || isSel ? 'var(--sms-acc-bg)' : 'transparent'; }}
                  >
                    {o.right ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                          {o.icon}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                        </span>
                        <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--sms-txt3)' }}>{o.right}</span>
                      </div>
                    ) : o.sublabel ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: isSel ? 'var(--sms-acc)' : 'var(--sms-txt)' }}>{o.icon}{o.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, fontStyle: 'normal', color: 'var(--sms-txt2)', marginTop: 2 }}>{o.sublabel}</div>
                      </>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{o.icon}{o.label}</span>
                    )}
                  </div>
                );
              })
          }
        </div>,
        document.body
      )}
    </div>
  );
}

// A country flag for an ISO 3166-1 alpha-2 code, via the bundled flag-icons
// SVG set (imported globally in App.jsx). Unicode flag *emoji* were tried
// first, but Windows' system fonts don't carry the regional-indicator glyphs
// most browsers rely on — Chrome/Edge on Windows renders the raw two-letter
// code as text instead of a flag, so an actual icon asset is used instead.
export function FlagIcon({ iso2, style }) {
  return <span className={`fi fi-${iso2.toLowerCase()}`} style={{ borderRadius: 2, flexShrink: 0, ...style }} aria-hidden="true" />;
}

// ── Intake capacity helpers ──────────────────────────────────────────────────
// Seats left in an intake — capacity minus everyone already active/pending there.
export function intakeRemaining(i) {
  return (Number(i.capacity) || 0) - (Number(i.occupied_count) || 0);
}

// Options for an intake SearchableSelect, scoped to one course, with full
// intakes left out entirely so staff can never pick one that's already full.
export function intakeOptionsFor(intakes, courseCode, { excludeIntakeCode, dateRange } = {}) {
  return intakes
    .filter(i => i.course_code === courseCode && i.intake_code !== excludeIntakeCode)
    .filter(i => intakeRemaining(i) > 0)
    .map(i => ({
      value: i.intake_code,
      label: i.intake_code,
      sublabel: dateRange && i.soc_date && i.eoc_date
        ? `${dateRange(i.soc_date, i.eoc_date)} · ${intakeRemaining(i)} seat(s) left`
        : `${intakeRemaining(i)} seat(s) left`,
    }));
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ show, onClose, title, children, size = 'md', tightHeader = false }) {
  useBodyScrollLock(show);
  if (!show) return null;
  const maxW = size === 'xl' ? 1100 : size === 'lg' ? 720 : size === 'sm' ? 400 : 560;
  // Portaled to document.body so position:fixed is always relative to the
  // viewport — a page ancestor with a running CSS animation/transform (e.g.
  // .stu-main-content's page-transition fade) otherwise becomes the fixed
  // containing block and traps/mis-centers the modal.
  return createPortal(
    <div className="sms-modal-backdrop">
      <div className="sms-modal" style={{ maxWidth: maxW }}>
        <div className={`d-flex align-items-center justify-content-between ${tightHeader ? 'mb-2' : 'mb-3'}`}>
          <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-text)' }}>{title}</h6>
          <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={onClose}
            aria-label="Close modal">
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Date Range Picker ────────────────────────────────────────────────────────
// Calendar-grid range picker (click a start day, then an end day) instead of
// two separate <input type="date">, for fields where seeing the days lined up
// on a month grid actually helps — e.g. a fee rate's effective date range.
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// 'YYYY-MM-DD' -> local-midnight Date, avoiding the UTC-parse day-rollback
// bug described in utils/format.js's fmtDate.
function fromISODate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

export function DateRangePicker({ startDate, endDate, onChange, dayNoun = 'days', placeholder = 'Select date range', disabled = false, alwaysBelow = false }) {
  const [open, setOpen] = useState(false);
  const start = fromISODate(startDate);
  const end = fromISODate(endDate);
  const [viewDate, setViewDate] = useState(start || new Date());
  const [panelPos, setPanelPos] = useState(null);
  const containerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const panelHeight = 320;

  // Portaled to document.body with position:fixed, same reasoning as
  // SearchableSelect's menu — a scrollable ancestor (here, .sms-modal's
  // overflow-y:auto) clips absolutely-positioned descendants regardless of
  // z-index, so only escaping its DOM subtree avoids the panel getting cut off.
  const updatePanelPos = React.useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const goUp = !alwaysBelow && spaceBelow < panelHeight + 8 && rect.top > spaceBelow;
    // Width shrinks to fit narrow viewports (e.g. a phone screen) instead of
    // staying a fixed 280px, and left is clamped so the panel never runs past
    // the right edge when the trigger sits close to it.
    const width = Math.min(280, window.innerWidth - 16);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setPanelPos({
      left: Math.max(8, left), width,
      top: goUp ? undefined : rect.bottom + 4,
      bottom: goUp ? (window.innerHeight - rect.top + 4) : undefined,
      // Pinned below: cap the panel to the room left so it scrolls inside
      // itself rather than running off the bottom of a short window.
      maxHeight: alwaysBelow ? Math.max(180, spaceBelow - 16) : undefined,
    });
  }, [alwaysBelow]);

  useEffect(() => {
    if (!open) return;
    // When the calendar is pinned below the field, bring the field into view
    // first so the panel has room instead of running off the screen bottom.
    if (alwaysBelow) containerRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
    updatePanelPos();
    const onDocClick = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)
          && panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', updatePanelPos, true);
    window.addEventListener('resize', updatePanelPos);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', updatePanelPos, true);
      window.removeEventListener('resize', updatePanelPos);
    };
  }, [open, updatePanelPos]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const dayCount = start && end ? Math.round((end - start) / 86400000) + 1 : 0;
  const cells = buildMonthGrid(viewDate.getFullYear(), viewDate.getMonth());
  const monthLabel = viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const handleDayClick = day => {
    if (!start || (start && end)) {
      onChange({ start: toISODate(day), end: null });
    } else if (day < start) {
      onChange({ start: toISODate(day), end: null });
    } else {
      onChange({ start: toISODate(start), end: toISODate(day) });
      setOpen(false);
    }
  };

  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const inRange = day => start && end && day > start && day < end;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button type="button" className="sms-form-control d-flex align-items-center justify-content-between"
        disabled={disabled}
        onClick={() => setOpen(o => !o)} style={{ textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.65 : 1 }}>
        <span style={{ color: start ? 'var(--sms-txt)' : 'var(--sms-txt3)' }}>
          {start ? `${fmtDate(startDate)}${end ? ` – ${fmtDate(endDate)}` : ' – …'}` : placeholder}
        </span>
        <i className="bi bi-calendar3" style={{ color: 'var(--sms-txt3)', fontSize: 13 }} />
      </button>

      {open && panelPos && createPortal(
        <div ref={panelRef} className="sms-daterange-panel" style={{
          position: 'fixed', left: panelPos.left, width: panelPos.width, zIndex: 2000,
          ...(panelPos.maxHeight ? { maxHeight: panelPos.maxHeight, overflowY: 'auto' } : {}),
          ...(panelPos.top !== undefined ? { top: panelPos.top } : { top: 'auto', bottom: panelPos.bottom }),
        }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '3px 8px' }}
              onClick={() => setViewDate(v => new Date(v.getFullYear(), v.getMonth() - 1, 1))}>
              <i className="bi bi-chevron-left" />
            </button>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{monthLabel}</span>
            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '3px 8px' }}
              onClick={() => setViewDate(v => new Date(v.getFullYear(), v.getMonth() + 1, 1))}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
          <div className="sms-daterange-dow">
            {DOW_LABELS.map(l => <span key={l}>{l}</span>)}
          </div>
          <div className="sms-daterange-grid">
            {cells.map((day, i) => {
              if (!day) return <span key={i} />;
              const isStart = isSameDay(day, start);
              const isEnd = isSameDay(day, end);
              const cls = ['sms-daterange-day'];
              if (isStart || isEnd) cls.push('selected');
              else if (inRange(day)) cls.push('in-range');
              return (
                <button type="button" key={i} className={cls.join(' ')} onClick={() => handleDayClick(day)}>
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="d-flex align-items-center justify-content-between mt-2" style={{ fontSize: 12 }}>
            <span style={{ color: 'var(--sms-txt2)' }}>{dayCount > 0 ? `${dayCount} ${dayNoun} selected` : 'Pick a start day, then an end day'}</span>
            <div className="d-flex gap-1">
              <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '3px 8px', fontSize: 12 }}
                onClick={() => {
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
                  handleDayClick(today);
                }}>
                Today
              </button>
              {(start || end) && (
                <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '3px 8px', fontSize: 12 }}
                  onClick={() => onChange({ start: null, end: null })}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Read-only detail view (row click for view-only permission users) ─────────
// fields: [{ label, value, full }] — value can be any React node; full spans both columns
export function DetailModal({ show, onClose, title, fields = [], size = 'md', children, header }) {
  return (
    <Modal show={show} onClose={onClose} size={size} title={
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="bi bi-info-circle-fill" style={{ color: 'var(--sms-acc)', fontSize: 16 }} aria-hidden="true" />
        {title}
      </span>
    }>
      {header && <div className="mb-3">{header}</div>}
      <div className="row g-2">
        {fields.map((f, i) => {
          const isEmpty = f.value === null || f.value === undefined || f.value === '';
          return (
            <div key={i} className={f.full ? 'col-12' : 'col-12 col-sm-6'}>
              <div className="sms-detail-field">
                <div className="sms-detail-label">{f.label}</div>
                <div className={`sms-detail-value${isEmpty ? ' empty' : ''}`}>
                  {isEmpty ? '—' : f.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {children && <div className="mt-3">{children}</div>}
      <div className="d-flex justify-content-end mt-4">
        <button type="button" className="sms-btn sms-btn-outline" onClick={onClose}>
          <i className="bi bi-x-lg" /> Close
        </button>
      </div>
    </Modal>
  );
}

// ── Detail panel (row click, slides in from the right like a modal —
// StudentsPage/TrainerDirectoryPage split view) ─────────────────────────────
// Unlike Modal, this stays mounted through its own close animation: `show`
// toggles a CSS class rather than unmounting immediately, so the panel (and
// the dimmed backdrop behind it) can slide/fade back out before disappearing.
// Clicking the backdrop closes it, same as clicking outside a modal. Callers
// should keep passing the same `fields`/`title`/`children` while `show` is
// false but the panel hasn't finished closing yet — i.e. don't null out the
// underlying record on close, only toggle `show` — so the content doesn't
// blank out mid-animation.
const DETAIL_PANEL_ANIM_MS = 280;

export function DetailPanel({ show, onClose, title, fields = [], children, header }) {
  const [mounted, setMounted] = useState(show);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let raf1, raf2, timer;
    if (show) {
      setMounted(true);
      // Two rAFs, not one — the panel must actually paint at translateX(100%)
      // first, otherwise the browser can coalesce the mount + the "open"
      // class into a single frame and the slide-in transition never plays.
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      timer = setTimeout(() => setMounted(false), DETAIL_PANEL_ANIM_MS);
    }
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(timer); };
  }, [show]);

  useBodyScrollLock(mounted);

  if (!mounted) return null;

  return createPortal(
    <div className={`sms-drawer-backdrop${visible ? ' open' : ''}`} onClick={onClose}>
      <div className={`sms-drawer-panel${visible ? ' open' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="sms-card">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2" style={{ color: 'var(--sms-text)' }}>
              <i className="bi bi-info-circle-fill" style={{ color: 'var(--sms-acc)', fontSize: 16 }} aria-hidden="true" />
              {title}
            </h6>
            <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={onClose}
              aria-label="Close details">
              <i className="bi bi-x-lg" />
            </button>
          </div>
          {header && <div className="mb-3">{header}</div>}
          <div className="row g-2">
            {fields.map((f, i) => {
              const isEmpty = f.value === null || f.value === undefined || f.value === '';
              return (
                <div key={i} className={f.full ? 'col-12' : 'col-12 col-sm-6'}>
                  <div className="sms-detail-field">
                    <div className="sms-detail-label">{f.label}</div>
                    <div className={`sms-detail-value${isEmpty ? ' empty' : ''}`}>
                      {isEmpty ? '—' : f.value}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {children && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Capacity resolution modal ────────────────────────────────────────────────
// Used after a bulk import or bulk transfer leaves some students without an
// intake assignment because their target was full. Lets staff pick a fresh
// course + intake per student; each row saves independently via the
// caller-supplied onAssign, and is removed from the list on success. Rows
// that fail (e.g. someone else filled the new intake first) show an inline
// error and stay so the user can retry with another intake.
export function CapacityResolveModal({
  show, onClose, title = 'Assign remaining students', rows = [], onResolved, courses = [], intakesForCourse, onAssign,
  description, resolvedMessage = 'All students have been assigned.',
}) {
  const [state, setState] = useState({}); // key -> { course_code, intake_code, saving, error }
  const [savingAll, setSavingAll] = useState(false);

  const getState = key => state[key] || { course_code: '', intake_code: '', saving: false, error: '' };
  const setRow = (key, patch) => setState(s => ({ ...s, [key]: { ...getState(key), ...patch } }));

  const save = async (row) => {
    const rs = getState(row.key);
    if (!rs.course_code || !rs.intake_code) {
      setRow(row.key, { error: 'Select a course and intake' });
      return;
    }
    setRow(row.key, { saving: true, error: '' });
    try {
      await onAssign(row, rs.course_code, rs.intake_code);
      onResolved(row.key);
    } catch (err) {
      setRow(row.key, { saving: false, error: err.response?.data?.message || 'Failed to assign — please try again' });
    }
  };

  // Saves every row that already has both fields picked, one after another
  // (sequential, not parallel — rows can compete for the same intake's
  // remaining seats, so letting one land before the next checks capacity
  // avoids overselling). Rows still missing a selection, or that fail, are
  // left in place same as a single Save.
  const saveAll = async () => {
    const ready = rows.filter(row => {
      const rs = getState(row.key);
      return rs.course_code && rs.intake_code && !rs.saving;
    });
    if (!ready.length) return;
    setSavingAll(true);
    for (const row of ready) {
      await save(row);
    }
    setSavingAll(false);
  };
  const readyCount = rows.filter(row => { const rs = getState(row.key); return rs.course_code && rs.intake_code; }).length;

  return (
    <Modal show={show} onClose={onClose} title={title} size="lg">
      <p style={{ fontSize: 13, color: 'var(--sms-txt2)', marginBottom: 14 }}>
        {rows.length
          ? (description || `${rows.length} student(s) couldn't be assigned because their intake was full. Choose another course and intake for each, then save all.`)
          : resolvedMessage}
      </p>
      {rows.length > 0 && (
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          <table className="sms-table" style={{ tableLayout: 'fixed', width: '100%', minWidth: 520 }}>
            <thead>
              <tr><th style={{ width: '30%' }}>Student</th><th style={{ width: '35%' }}>Course</th><th style={{ width: '35%' }}>Intake</th></tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const rs = getState(row.key);
                return (
                  <tr key={row.key}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.name}</div>
                      {row.detail && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 2 }}>{row.detail}</div>}
                    </td>
                    <td>
                      <SearchableSelect value={rs.course_code} placeholder="-- Select course --" disabled={rs.saving || savingAll}
                        onChange={e => setRow(row.key, { course_code: e.target.value, intake_code: '', error: '' })}
                        options={courses.map(c => ({ value: c.course_code, label: c.course_name }))} />
                    </td>
                    <td>
                      <SearchableSelect value={rs.intake_code} disabled={!rs.course_code || rs.saving || savingAll}
                        placeholder={rs.course_code ? '-- Select intake --' : '-- Select course first --'}
                        onChange={e => setRow(row.key, { intake_code: e.target.value, error: '' })}
                        options={rs.course_code ? intakesForCourse(rs.course_code) : []} />
                      {rs.error && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>{rs.error}</div>}
                      {rs.saving && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}><InlineSpinner size={11} /> Saving…</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex justify-content-end gap-2 mt-3">
        {rows.length > 0 && (
          <button type="button" className="sms-btn sms-btn-success" style={{ fontSize: 12.5 }}
            disabled={savingAll || !readyCount} onClick={saveAll}>
            {savingAll ? <InlineSpinner /> : <i className="bi bi-check2-all" />} Save All{readyCount ? ` (${readyCount})` : ''}
          </button>
        )}
        <button type="button" className="sms-btn sms-btn-outline" disabled={savingAll} onClick={onClose}>
          <i className="bi bi-x-lg" /> Close
        </button>
      </div>
    </Modal>
  );
}

// ── Confirm Dialog ───────────────────────────────────────────────────────────
export function ConfirmDialog({ show, onConfirm, onCancel, message = 'Are you sure?', danger = false, error = '', confirmLabel }) {
  useBodyScrollLock(show);
  if (!show) return null;
  return createPortal(
    <div className="sms-modal-backdrop">
      <div className="sms-modal" style={{ maxWidth: 380 }}>
        <div className="text-center mb-3">
          <i className={`bi ${danger ? 'bi-exclamation-triangle-fill' : 'bi-question-circle-fill'}`}
             style={{ fontSize: 32, color: danger ? '#ef4444' : 'var(--sms-accent)' }} />
        </div>
        <p style={{ color: 'var(--sms-text)', textAlign: 'center', marginBottom: error ? 12 : 20 }}>{message}</p>
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8,
            padding: '8px 12px', marginBottom: 20, fontSize: 12.5, color: 'var(--sms-bad-strong)', textAlign: 'center',
          }}>{error}</div>
        )}
        <div className="d-flex gap-2 justify-content-center">
          <button className="sms-btn sms-btn-outline" onClick={onCancel}>Cancel</button>
          <button className={`sms-btn ${danger ? 'sms-btn-danger' : 'sms-btn-primary'}`} onClick={onConfirm}>
            {confirmLabel || (danger ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ value, map }) {
  const cfg = map[value] || { label: value, cls: '' };
  return (
    <span className={`badge rounded-pill ${cfg.cls}`} style={{ fontSize: 12 }}>
      {cfg.label}
    </span>
  );
}

export const statusBadgeMap = {
  registered: { label: 'Registered', cls: 'badge-late' },
  active:     { label: 'Active',     cls: 'badge-active' },
  inactive:   { label: 'Inactive',   cls: 'badge-inactive' },
  graduated:  { label: 'Graduated',  cls: 'badge-graduated' },
  withdrawn:  { label: 'Withdrawn',  cls: 'badge-withdrawn' },
  deferred:   { label: 'Deferred',   cls: 'badge-deferred' },
};

export const attendanceBadgeMap = {
  present: { label: 'Present', cls: 'badge-present' },
  late:    { label: 'Late',    cls: 'badge-late' },
  absent:  { label: 'Absent',  cls: 'badge-absent' },
  mc:      { label: 'MC',      cls: 'badge-mc' },
  pending: { label: 'Pending', cls: 'badge-inactive' },
};

export const sessionTypeBadgeMap = {
  training:   { label: 'Training',   cls: 'badge-active' },
  assessment: { label: 'Assessment', cls: 'badge-graduated' },
};

export const requestBadgeMap = {
  new:       { label: 'New',       cls: 'badge-active' },
  pending:   { label: 'Pending',   cls: 'badge-late' },
  in_review: { label: 'In Review', cls: 'badge-active' },
  approved:  { label: 'Acknowledge', cls: 'badge-present' },
  rejected:  { label: 'Rejected',  cls: 'badge-absent' },
  cancelled: { label: 'Cancelled', cls: 'badge-inactive' },
};

export const severityBadgeMap = {
  low:    { label: 'Low',    cls: 'badge-active' },
  medium: { label: 'Medium', cls: 'badge-late' },
  high:   { label: 'High',   cls: 'badge-absent' },
};

export const disciplinaryStatusBadgeMap = {
  open:     { label: 'Open',     cls: 'badge-absent' },
  resolved: { label: 'Resolved', cls: 'badge-present' },
  appealed: { label: 'Appealed', cls: 'badge-late' },
};

export const observationRatingBadgeMap = {
  excellent:          { label: 'Excellent',          cls: 'badge-present' },
  satisfactory:        { label: 'Satisfactory',        cls: 'badge-late' },
  needs_improvement:   { label: 'Needs Improvement',   cls: 'badge-absent' },
};

export const assessmentStatusBadgeMap = {
  draft:           { label: 'Draft',          cls: 'badge-inactive' },
  pending_review:  { label: 'Pending Review', cls: 'badge-late' },
  returned:        { label: 'Returned',       cls: 'badge-absent' },
  graded:          { label: 'Graded',         cls: 'badge-present' },
};

export const claimStatusBadgeMap = {
  under_review: { label: 'Pending Verification', cls: 'badge-late' },
  verified:     { label: 'Pending Approval', cls: 'badge-graduated' },
  cancelled:    { label: 'Cancelled',     cls: 'badge-inactive' },
  approved:     { label: 'Approved',      cls: 'badge-active' },
  rejected:     { label: 'Rejected',      cls: 'badge-absent' },
  paid:         { label: 'Paid',          cls: 'badge-present' },
};

export const sessionStatusBadgeMap = {
  scheduled: { label: 'Upcoming', cls: 'badge-active' },
  ongoing:   { label: 'Ongoing',  cls: 'badge-late' },
  ended:     { label: 'Completed', cls: 'badge-present' },
};

export const conflictBadgeMap = {
  trainer: { label: 'Trainer double-booked', cls: 'badge-absent' },
  venue:   { label: 'Venue double-booked',   cls: 'badge-absent' },
};

// ── Spinner ──────────────────────────────────────────────────────────────────
// react-spinners HashLoader everywhere loading UI is shown (full-section and
// inline). Its color prop is parsed by a JS hex/rgb() parser, not the
// browser, so a raw var(--sms-gold)/currentColor string fails to parse and
// silently drops one of the two loader elements. Reading the resolved color
// via getComputedStyle sidesteps that while still tracking var(--sms-gold),
// which is retuned per theme in index.css to clear WCAG AA contrast against
// both the light and dark surface colors (verified 5:1+ on light, 11:1+ on dark).
// `minHeight` defaults to a near-full-page 60vh, right for a page's own
// top-level loading state. Pass a smaller value (e.g. '160px') when the
// spinner sits inside something already-sized, like a card slotted into a
// table body or modal — otherwise it stretches that container to 60vh too.
export function Spinner({ size = 50, minHeight = '60vh' }) {
  const { theme } = useTheme();
  const [color, setColor] = React.useState('#b45309');
  React.useLayoutEffect(() => {
    setColor(getComputedStyle(document.documentElement).getPropertyValue('--sms-gold').trim());
  }, [theme]);
  return (
    <div className="d-flex justify-content-center align-items-center p-4" style={{ minHeight }} role="status">
      <HashLoader size={size} color={color} />
      <span className="visually-hidden">Loading…</span>
    </div>
  );
}

// Loading state for a table's <tbody> that keeps the <thead> (and the
// table's own shape/scroll container) on screen instead of swapping the
// whole card out for a big centered Spinner — the header never disappears,
// there's no tall-then-short layout jump between loading and loaded, and no
// scrollbar flicker from the container's height swinging around while data
// is still in flight. `columns` must match the real <thead>'s th count.
// Bar widths vary a little per cell (deterministic, not random) so the rows
// read as placeholder content rather than a solid gray block.
export function TableSkeletonRows({ columns, rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="sms-skel-row" aria-hidden="true">
          {Array.from({ length: columns }).map((_, c) => (
            <td key={c}><span className="sms-skel-bar" style={{ width: `${55 + ((r * 7 + c * 13) % 35)}%` }} /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Small inline variant for buttons ("Saving…", "Submitting…", etc), replacing
// Bootstrap's spinner-border everywhere it was used for the same purpose.
// Resolves the surrounding text color itself (see Spinner comment above for why).
export function InlineSpinner({ size = 14, style }) {
  const ref = React.useRef(null);
  const [color, setColor] = React.useState('#ffffff');
  React.useLayoutEffect(() => {
    if (ref.current) setColor(getComputedStyle(ref.current).color);
  }, []);
  return (
    <span ref={ref} style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}>
      <HashLoader size={size} color={color} />
    </span>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon = 'bi-inbox', message = 'No records found' }) {
  return (
    <div className="text-center py-5" style={{ color: 'var(--sms-hint)' }}>
      <i className={`bi ${icon}`} style={{ fontSize: 40 }} />
      <p className="mt-2 mb-0" style={{ fontSize: 14 }}>{message}</p>
    </div>
  );
}

// ── Page Header ──────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
      <div>
        <h1 className="sms-page-title">{title}</h1>
        {subtitle && <p className="sms-page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="d-flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

// ── Search + Filter bar ──────────────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Search…', children }) {
  return (
    <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
      <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-hint)', fontSize: 13 }} />
        <input className="sms-form-control" style={{ paddingLeft: 30 }}
          value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      </div>
      {children}
    </div>
  );
}

// ── Search + filter bar, single responsive line ──────────────────────────────
// One card containing the search input plus any number of <FilterSelect>
// children plus an (optional) Clear button — all in one flex-wrap row so they
// share a line on wide screens and drop to their own line on narrow ones,
// instead of the search box and filter row living in separate cards/rows.
// `bare` drops the card chrome (border/shadow/background) for callers that
// already sit inside their own card — e.g. a filter row nested in a card body
// — so it doesn't render as a redundant box-within-a-box.
export function FilterBar({ search, onSearch, searchPlaceholder = 'Search…', onClear, children, bare = false }) {
  return (
    <div className={bare ? 'mb-3' : 'sms-card mb-3'} style={bare ? undefined : { padding: '12px 14px' }}>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt3)', fontSize: 13 }} aria-hidden="true" />
          <input className="sms-form-control" style={{ paddingLeft: 28 }}
            placeholder={searchPlaceholder} value={search} onChange={e => onSearch(e.target.value)} />
        </div>
        {children}
        {onClear && (
          <button type="button" className="sms-btn sms-btn-outline-danger" style={{ flex: '0 0 auto' }} onClick={onClear}>
            <i className="bi bi-x-circle" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}

// One filter dropdown sized to sit alongside FilterBar's search input/others.
export function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <div style={{ flex: '1 1 160px', minWidth: 150 }}>
      <SearchableSelect value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} options={options} />
    </div>
  );
}

// ── Sort hook ────────────────────────────────────────────────────────────────
export function useSort(data, defaultKey = null, defaultDir = 'asc') {
  // key + dir are combined into one state value so toggling is a single, pure
  // update — no nested setState side effects (StrictMode double-invokes updater
  // functions in dev, which previously caused direction toggles to cancel out).
  const [sort, setSort] = React.useState({ key: defaultKey, dir: defaultDir });

  const toggleSort = React.useCallback((key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }
    );
  }, []);

  const sorted = React.useMemo(() => {
    if (!sort.key) return data;
    return [...data].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [data, sort]);

  return { sorted, sortKey: sort.key, sortDir: sort.dir, toggleSort };
}

// ── Sortable <th> ─────────────────────────────────────────────────────────────
export function SortTh({ label, field, sortKey, sortDir, onSort, style }) {
  const active = sortKey === field;
  const iconClass = active ? (sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up';
  return (
    <th
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {label}
        <i className={`bi ${iconClass}`} aria-hidden="true"
          style={{ fontSize: 13, color: active ? 'var(--sms-acc)' : 'var(--sms-txt3)' }} />
      </span>
    </th>
  );
}

// ── Pagination hook ───────────────────────────────────────────────────────────
const ALL_SENTINEL = 999999;

export function usePagination(data, defaultPageSize = 10) {
  const [page, setPage]         = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  // Reset to page 1 whenever data length or pageSize changes
  React.useEffect(() => { setPage(1); }, [data.length, pageSize]);

  const showAll    = pageSize === ALL_SENTINEL;
  const totalPages = showAll ? 1 : Math.max(1, Math.ceil(data.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const start      = showAll ? 0 : (safePage - 1) * pageSize;
  const paged      = showAll ? data : data.slice(start, start + pageSize);

  return { paged, page: safePage, setPage, pageSize, setPageSize, totalPages, total: data.length, start };
}

// ── Pagination UI ─────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [
  { label: '10',  value: 10 },
  { label: '20',  value: 20 },
  { label: '50',  value: 50 },
  { label: '100', value: 100 },
  { label: 'All', value: ALL_SENTINEL },
];

export function Pagination({ page, totalPages, total, pageSize, setPage, setPageSize, start, idSuffix = '' }) {
  if (total === 0) return null;

  const showAll = pageSize === ALL_SENTINEL;
  const end     = showAll ? total : Math.min(start + pageSize, total);

  function buildPageRange(current, last) {
    if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
    const range = new Set([1, last, current]);
    if (current > 1) range.add(current - 1);
    if (current < last) range.add(current + 1);
    const sorted = [...range].sort((a, b) => a - b);
    const result = [];
    let prev = null;
    for (const n of sorted) {
      if (prev !== null && n - prev > 1) result.push('…');
      result.push(n);
      prev = n;
    }
    return result;
  }

  const pages = showAll ? [] : buildPageRange(page, totalPages);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 10,
      padding: '10px 14px',
      borderTop: '1px solid var(--sms-bdr)',
      background: 'var(--sms-sur)',
      borderRadius: '0 0 10px 10px',
    }}>
      {/* Left: rows per page + record count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label htmlFor={`pg-size${idSuffix}`} style={{ fontSize: 12, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }}>
            Rows per page
          </label>
          <select
            id={`pg-size${idSuffix}`}
            value={pageSize}
            onChange={e => setPageSize(parseInt(e.target.value))}
            style={{
              fontSize: 12, padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--sms-bdr)',
              background: 'var(--sms-sur)', color: 'var(--sms-txt)',
              cursor: 'pointer',
            }}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }}>
          {showAll ? `All ${total}` : `${start + 1}–${end} of ${total}`}
        </span>
      </div>

      {/* Right: page buttons (hidden when showing all) */}
      {!showAll && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} role="navigation" aria-label="Pagination">
          <PgBtn onClick={() => setPage(1)} disabled={page <= 1} aria-label="First page">
            <i className="bi bi-chevron-double-left" aria-hidden="true" />
          </PgBtn>
          <PgBtn onClick={() => setPage(p => p - 1)} disabled={page <= 1} aria-label="Previous page">
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </PgBtn>

          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`e${i}`} style={{ fontSize: 12, color: 'var(--sms-txt3)', padding: '0 2px' }} aria-hidden="true">…</span>
            ) : (
              <PgBtn key={p} onClick={() => setPage(p)} active={p === page}
                aria-label={`Page ${p}`} aria-current={p === page ? 'page' : undefined}>
                {p}
              </PgBtn>
            )
          )}

          <PgBtn onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} aria-label="Next page">
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </PgBtn>
          <PgBtn onClick={() => setPage(totalPages)} disabled={page >= totalPages} aria-label="Last page">
            <i className="bi bi-chevron-double-right" aria-hidden="true" />
          </PgBtn>
        </div>
      )}
    </div>
  );
}

function PgBtn({ children, onClick, disabled, active, ...rest }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      {...rest}
      style={{
        minWidth: 30, height: 30, borderRadius: 6, border: '1px solid',
        borderColor: active ? 'var(--sms-btn-primary-bg)' : 'var(--sms-txt3)',
        background:  active ? 'var(--sms-btn-primary-bg)' : 'var(--sms-sur)',
        color:       active ? '#ffffff' : disabled ? 'var(--sms-txt2)' : 'var(--sms-txt)',
        fontSize: 12, fontWeight: active ? 600 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .15s',
      }}
    >
      {children}
    </button>
  );
}
